import { useMutation } from '@tanstack/react-query';
import * as service from '../services';
import { useOmnichannelStore } from '../store/ModuloOmnichannel.store';
import { useDebounce } from '../../../../../hooks';
import { FilterType } from '../types';

type SelectItem = {
  label: string;
  value: string;
};

type FilterResponse = {
  key: string;
  datapoints?: number;
};

type SelectList = { list: SelectItem[]; cache: SelectItem[]; value: string[] };

const nameBody = {
  rede: 'redes',
  cidade: 'cidades',
  uf: 'ufs',
};

export const useOmnichannelFilter = () => {
  const debounce = useDebounce();

  const rede = useOmnichannelStore(state => state.rede);
  const cidade = useOmnichannelStore(state => state.cidade);
  const uf = useOmnichannelStore(state => state.uf);
  const setOmnichannelState = useOmnichannelStore(state => state.setOmnichannelState);
  const setOmnichannelMutateRequestBody = useOmnichannelStore(state => state.setOmnichannelMutateRequestBody);

  const FILTER_TYPE_SET_STATE = {
    rede: (data: SelectList) => setOmnichannelState('rede', data),
    cidade: (data: SelectList) => setOmnichannelState('cidade', data),
    uf: (data: SelectList) => setOmnichannelState('uf', data),
  };

  const FILTER_TYPE_STATE = {
    rede: rede as SelectList,
    cidade: cidade as SelectList,
    uf: uf as SelectList,
  };

  const filterSelectItems = (list: FilterResponse[]) => {
    return (list || []).map(item => ({
      label: item.key,
      value: item.key,
    }));
  };

  const setList = (name: FilterType, list: any[]) => {
    const newList: SelectItem[] = filterSelectItems(list);

    const removeDuplicate = newList.filter(i => !FILTER_TYPE_STATE[name].value.includes(i.value));
    const concatList = FILTER_TYPE_STATE[name].value.length ? FILTER_TYPE_STATE[name].cache.concat(removeDuplicate) : newList;

    const cache = concatList.filter(item => FILTER_TYPE_STATE[name].value.includes(item.value));
    FILTER_TYPE_SET_STATE[name]({ ...FILTER_TYPE_STATE[name], list: concatList, cache });
    syncStoreWithCache(name, cache);
  };

  const syncStoreWithCache = (name: FilterType, cache: SelectItem[]) => {
    const list = cache.map(i => i.value).filter(Boolean) as string[];
    setOmnichannelMutateRequestBody(nameBody[name], list);
  };

  const handleSelectFilter = (name: FilterType, values: string[]) => {
    const cache = FILTER_TYPE_STATE[name].list.filter(item => values.includes(item.value));
    FILTER_TYPE_SET_STATE[name]({ ...FILTER_TYPE_STATE[name], list: FILTER_TYPE_STATE[name].list, cache, value: values });
    syncStoreWithCache(name, cache);
  };

  const handleClearFilters = (name: FilterType) => {
    FILTER_TYPE_SET_STATE[name]({ ...FILTER_TYPE_STATE[name], list: [], cache: [], value: [] });
    setOmnichannelMutateRequestBody(nameBody[name], []);
  };

  const getProductsByRange = async (name: FilterType, data: any) => {
    const res = await service.getItensFiltros(data);
    if (res?.length) {
      setList(name, res);
      return res;
    }
  };

  const mutation = useMutation({
    mutationFn: ({ name, data }: { name: FilterType; data: any }) => getProductsByRange(name, data),
  });

  const onSearchFilter = (name: string, inputValue: string) => {
    let cliente = '';
    try {
      cliente = JSON.parse(localStorage.getItem('cliente') ?? '{}')?.codigo ?? '';
    } catch {
      cliente = '';
    }

    const body = {
      field: name,
      query: inputValue,
      cliente,
      rede: name !== 'rede' && rede.value?.length ? rede.value : null,
      uf: name !== 'uf' && uf.value?.length ? uf.value : null,
      cidade: name !== 'cidade' && cidade.value?.length ? cidade.value : null,
    };

    debounce(() => {
      mutation.mutate({ name: name as FilterType, data: body });
    }, 500);
  };

  return {
    rede,
    cidade,
    uf,
    filterLoading: mutation.isPending,
    filterHandlers: {
      handleRequestFilterList: mutation.mutateAsync,
      onSearchFilter,
      handleSelectFilter,
      handleClearFilters,
    },
  };
};
